// Library hooks
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWRInfinite from "swr/infinite";
import { useIntersection } from "react-use";
import PullToRefresh from "react-simple-pull-to-refresh";
import {
  FaArrowDownLong,
  FaArrowUpLong,
  FaChevronRight,
} from "react-icons/fa6";
import "./style.css";

// Novel
import {
  Box,
  Center,
  HStack,
  Link,
  Spacer,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { query_recursive_types as RpcRecursiveTypes } from "@/infra/api/rpc/api";
// Usecase
import { NovelUseCase } from "@/usecases/novelUseCase";

// Hooks
import { useAuth } from "@/hooks/useAuth";
import { useLayoutUI } from "@/hooks/useLayoutUI";
import { useAppParameters } from "@/hooks/useAppParameters";
import { useMustAcknowledgeGuidelines } from "@/hooks/useMustAcknowledgeGuidelines";

// Components
import { AppHeader } from "@/components/common/header/appHeader";
import { ActivityModal } from "@/components/common/activityModal";
import { ImageHasBeenReplacedModal } from "@/components/common/imageHasBeenReplacedModal";
import { MustAcknowledgeModal } from "@/components/common/mustAcknowledgeModal";
import { NoResults } from "@/components/common/noResults";
import { NovelCell } from "@/components/novel/novelCell";
import { StoryTypeModal } from "@/components/common/storyTypeModal";
import { logError } from "@/utils/utils";
import { useGuidelinesModalForNewUser } from "@/hooks/useGuidelinesModalForNewUser";
import { GuidelinesModalForNewUser } from "@/components/common/guidelinesModalForNewUser";
import {
  BOTTOM_TABS_HEIGHT,
  BottomTabs,
} from "@/components/common/bottomTabs/bottomTabs";
import { useHandleStoryTypeModal } from "@/hooks/useHandleStoryTypeModal";
import {
  HEADER_HEIGHT_LARGE,
  HEADER_HEIGHT_SMALL,
} from "@/components/common/header/header";
import { NavigationHeaderContainer } from "@/containers/navigationHeaderContainer";
import { useFetchMoreRSS } from "./use-fetch-more-rss";
import { RSSCarousel } from "./rssCarousel";

const NOTICE_HEIGHT = "48px";
const ANNOUNCEMENT_WRAPPER_HEIGHT = "80px"; // 48px + 16px*2 padding
const OLD_EDITOR_ANNOUNCEMENT_URL =
  "https://teller.jp/announcement/old-editor-end";

const novelUseCase = new NovelUseCase();

type OldEditorAnnouncementBannerProps = {
  canOpenInAppWeb: boolean;
};

const OldEditorAnnouncementBanner: React.FC<
  OldEditorAnnouncementBannerProps
> = ({ canOpenInAppWeb }) => {
  const href = canOpenInAppWeb
    ? `teller-studio://in-app-webview?url=${OLD_EDITOR_ANNOUNCEMENT_URL}`
    : OLD_EDITOR_ANNOUNCEMENT_URL;

  return (
    <Box bg="backgroundAbsolutely" w="100%" p="16px">
      <Link
        href={href}
        target={canOpenInAppWeb ? "_self" : "_blank"}
        w="100%"
        display="block"
        _hover={{ textDecoration: "none" }}
      >
        <HStack
          bg="#fee1e2"
          border="1px solid"
          borderColor="contentNotification"
          borderRadius="8px"
          h="48px"
          pl="12px"
          pr="8px"
          gap="0"
          w="100%"
        >
          <Text
            flex="1"
            fontSize={14}
            fontWeight="bold"
            color="contentNotification"
            lineHeight="1.25"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            【重要】旧エディター提供終了のお知らせ
          </Text>
          <Center w="16px" h="16px" flexShrink={0} color="contentNotification">
            <FaChevronRight size={14} />
          </Center>
        </HStack>
      </Link>
    </Box>
  );
};

const PullingContent = (
  <Center w="100%" py="2">
    <HStack gap="2" color="contentTertiary">
      <FaArrowDownLong size={16} />
      <Text fontSize={14} fontWeight={600}>
        引き下げて更新
      </Text>
      <Spacer w="24px" />
    </HStack>
  </Center>
);

const RefreshingContent = (
  <Center w="100%" py="2">
    <HStack gap="2" color="contentTertiary">
      <FaArrowUpLong size={16} />
      <Text fontSize={14} fontWeight={600}>
        引き下げて更新
      </Text>
      <Spinner size="sm" boxSize="24px" />
    </HStack>
  </Center>
);

export const HomeContainer: React.FC = () => {
  const { setLayout } = useLayoutUI();

  const [isLoading, setIsLoading] = useState(true);
  const { isApp, canUseReactNativeStudio, canOpenInAppWeb } =
    useAppParameters();
  const { storyTypeModalDisclosure, toWriteChatNovel, toWriteNovel } =
    useHandleStoryTypeModal();

  const { tellerUser, isOfficialWriter } = useAuth();
  const navigate = useNavigate();

  const paginationRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const getKey = useCallback(
    (_: number, prevData: RpcRecursiveTypes.IMySeriesCursorResponse | null) => {
      if (!tellerUser?.id?.value) {
        return null;
      }
      if (!prevData) {
        return ["/api/user/novels", ""];
      }
      if (prevData.cursor?.endCursor?.value) {
        return ["/api/user/novels", prevData.cursor.endCursor.value];
      }
      return null;
    }, // SWR key
    [tellerUser],
  );

  const fetcher = useCallback(
    ([_key, cursor]: [
      string,
      string,
    ]): Promise<RpcRecursiveTypes.IMySeriesCursorResponse | null> =>
      novelUseCase.fetchUserNovels(cursor),
    [],
  );

  const {
    data: novelPages,
    error,
    size,
    setSize,
    isValidating,
    mutate,
  } = useSWRInfinite(getKey, fetcher);

  const isLoadingFirstPage = !novelPages && !error;

  useEffect(() => {
    if (error) {
      logError(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      isLoadingFirstPage ||
      (size > 0 && novelPages && !novelPages[size - 1])
    ) {
      setIsLoading(true);
    } else {
      // Delay reset loading state so we don't trigger multiple page request at almost the same time
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [isLoadingFirstPage, novelPages, size]);

  const isEmpty =
    novelPages?.[0] === null || novelPages?.[0]?.seriesList?.length === 0;
  const isLastPage = novelPages?.[size - 1]?.cursor?.endCursor?.value === "";

  const intersection = useIntersection(paginationRef, {
    root: null,
    rootMargin: "0px",
    threshold: 1,
  });

  useEffect(() => {
    if (
      intersection?.intersectionRatio === 1 &&
      !isValidating &&
      !isLastPage &&
      !isLoading
    ) {
      setSize(size + 1);
    }
  }, [
    intersection?.intersectionRatio,
    isLastPage,
    isLoading,
    isValidating,
    setSize,
    size,
  ]);

  const layoutSet = useRef(false);
  useEffect(() => {
    if (!layoutSet.current) {
      setLayout({
        title: "自分の作品",
      });
      layoutSet.current = true;
    }
  }, [layoutSet, setLayout]);

  const {
    novelId: guidelinesAcknowledgeNeededNovelId,
    isImageReplacedModalOpen,
    openImageReplacedModal,
    closeImageReplacedModal,
    isGuidelinesAcknowledgeModalOpen,
    closeGuidelinesAcknowledgedModal,
  } = useMustAcknowledgeGuidelines();

  const {
    isOpen: isOpenGuidelinesModalForNewUser,
    handleOnEnter: handleOnEnterGuidelinesModalForNewUser,
  } = useGuidelinesModalForNewUser();

  const openReactNativeStudio = useCallback(() => {
    window.location.href = "teller-studio://switch-studio-version";
  }, []);

  const { data: rssItems } = useFetchMoreRSS();

  return (
    <VStack
      minH="100svh"
      w="100%"
      h="100%"
      bg="backgroundPrimary"
      gap="0"
      pb="env(safe-area-inset-bottom)"
    >
      {isApp ? (
        <AppHeader title="自分の作品" noBackButton moreButton={false}>
          {canUseReactNativeStudio && (
            <HStack
              bg="contentLink"
              h={NOTICE_HEIGHT}
              w="100%"
              pl="12px"
              pr="8px"
              gap="0"
              cursor="pointer"
              onClick={openReactNativeStudio}
            >
              <Text
                flex="1"
                fontSize={14}
                fontWeight="bold"
                color="white"
                lineHeight="1.5"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                新しいエディターに切り替える
              </Text>
              <Center w="16px" h="16px" flexShrink={0} color="white">
                <FaChevronRight size={14} />
              </Center>
            </HStack>
          )}
          <OldEditorAnnouncementBanner canOpenInAppWeb={canOpenInAppWeb} />
        </AppHeader>
      ) : (
        <NavigationHeaderContainer
          flexShrink={0}
          onClickWriteStory={storyTypeModalDisclosure.onOpen}
        />
      )}

      <Center flex={1} w="100%" h="100%">
        <Box w="100%" maxW="1100px">
          <PullToRefresh
            onRefresh={() => mutate().then(() => {})}
            pullingContent={PullingContent}
            refreshingContent={RefreshingContent}
          >
            <VStack
              w="100%"
              h="100%"
              maxW="1100px"
              overflowY="scroll"
              scrollbarWidth="none"
              ref={contentRef}
              gap="0"
            >
              {rssItems && <RSSCarousel isApp={isApp} items={rssItems} />}

              {isEmpty ? (
                <NoResults text="ノベルは見つかりませんでした" />
              ) : null}
              {!novelPages ? null : (
                <VStack
                  w="100%"
                  gap="0"
                  pb={{
                    base: `calc(env(safe-area-inset-bottom) + ${BOTTOM_TABS_HEIGHT} + ${HEADER_HEIGHT_SMALL} + ${isApp ? ANNOUNCEMENT_WRAPPER_HEIGHT : "0px"} + ${isApp && canUseReactNativeStudio ? NOTICE_HEIGHT : "0px"})`,
                    lg: `calc(env(safe-area-inset-bottom) + ${HEADER_HEIGHT_LARGE} + ${isApp ? ANNOUNCEMENT_WRAPPER_HEIGHT : "0px"} + ${isApp && canUseReactNativeStudio ? NOTICE_HEIGHT : "0px"})`,
                  }}
                >
                  {novelPages?.map((novels) =>
                    novels?.seriesList?.map(
                      (novel: RpcRecursiveTypes.ISeriesResponse) => (
                        <NovelCell
                          key={novel.id?.value}
                          novel={novel}
                          isOfficialWriter={isOfficialWriter}
                          onGuidelinesCheckNeeded={openImageReplacedModal}
                        />
                      ),
                    ),
                  )}
                  <div ref={paginationRef} />
                </VStack>
              )}
            </VStack>
          </PullToRefresh>
        </Box>

        {isLoading || isValidating ? <ActivityModal /> : null}

        {guidelinesAcknowledgeNeededNovelId && (
          <>
            <ImageHasBeenReplacedModal
              open={isImageReplacedModalOpen}
              onClose={closeImageReplacedModal}
            />
            <MustAcknowledgeModal
              open={isGuidelinesAcknowledgeModalOpen}
              onClose={closeGuidelinesAcknowledgedModal}
            />
          </>
        )}
      </Center>

      <BottomTabs
        selectedTab="home"
        position="fixed"
        bottom="0"
        display={{
          base: "flex",
          lg: "none",
        }}
        onPressTab={(tab) => {
          switch (tab) {
            case "home":
              // nop
              break;
            case "contest":
              navigate("/contests?notransition=1");
              break;
            case "write":
              storyTypeModalDisclosure.onOpen();
              break;
            default:
              // nop
              break;
          }
        }}
      />

      <GuidelinesModalForNewUser
        open={isOpenGuidelinesModalForNewUser}
        onEnterButton={handleOnEnterGuidelinesModalForNewUser}
      />

      <StoryTypeModal
        open={storyTypeModalDisclosure.open}
        onClose={storyTypeModalDisclosure.onClose}
        onChatClicked={toWriteChatNovel}
        onNovelClicked={toWriteNovel}
      />
    </VStack>
  );
};
